import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  CircularProgress,
  Fab,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import { aiAssistantService, AiChatMessage } from "../../service/AiAssistantService";
import { isRtl } from "../../i18n/config";
import { buildAssistantContext } from "../../helper/helpArticles";

// The browser Web Speech API is not in the DOM lib types; grab it loosely.
const getSpeechRecognition = (): any =>
  (typeof window !== "undefined" &&
    ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)) ||
  null;

/**
 * Floating in-app help assistant. The user can type or talk (speech-to-text via the
 * browser Web Speech API), and can optionally have replies read aloud (text-to-speech).
 * Backed by api/AiAssistant/sendAssistantMessage (Azure OpenAI). Stateless per session:
 * the conversation lives in component state only.
 */
export const AiAssistantWidget: React.FC = () => {
  const { t, i18n } = useTranslation();
  const rtl = isRtl(i18n.language);
  const speechLang = i18n.language?.startsWith("ar") ? "ar-EG" : "en-US";

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [speakEnabled, setSpeakEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<any>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const speechSupported = useMemo(() => !!getSpeechRecognition(), []);
  const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;

  // Keep the message list scrolled to the newest reply.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, open]);

  // Stop any in-flight speech / recognition when the widget closes or unmounts.
  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.stop();
        if (ttsSupported) window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    };
  }, [ttsSupported]);

  const speak = (text: string) => {
    if (!speakEnabled || !ttsSupported || !text) return;
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = speechLang;
      window.speechSynthesis.speak(utter);
    } catch {
      /* ignore */
    }
  };

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || loading) return;

    setError(null);
    const nextMessages: AiChatMessage[] = [...messages, { role: "user", content }];
    setMessages(nextMessages);
    setInput("");
    setLoading(true);

    try {
      // Ground the answer in the platform's own Help Center: retrieve the most relevant
      // help articles for this question (client-side) and pass their text as context.
      const context = buildAssistantContext(content);
      const reply = await aiAssistantService.SendMessage({
        messages: nextMessages,
        language: i18n.language,
        context,
      });
      const replyText = reply || t("aiAssistant.noReply");
      setMessages((prev) => [...prev, { role: "assistant", content: replyText }]);
      speak(replyText);
    } catch (e: any) {
      setError(e?.message || t("aiAssistant.error"));
    } finally {
      setLoading(false);
    }
  };

  const toggleListening = () => {
    if (!speechSupported) return;

    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }

    const SpeechRecognition = getSpeechRecognition();
    const recognition = new SpeechRecognition();
    recognition.lang = speechLang;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: any) => {
      const transcript = event.results?.[0]?.[0]?.transcript ?? "";
      if (transcript) send(transcript);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);

    recognitionRef.current = recognition;
    setListening(true);
    recognition.start();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  if (!open) {
    return (
      <Tooltip title={t("aiAssistant.title")} placement="left">
        <Fab
          color="primary"
          onClick={() => setOpen(true)}
          aria-label={t("aiAssistant.title")}
          sx={{ position: "fixed", bottom: 74, [rtl ? "left" : "right"]: 24, zIndex: 1300 }}
        >
          <SmartToyIcon />
        </Fab>
      </Tooltip>
    );
  }

  return (
    <Paper
      elevation={8}
      dir={rtl ? "rtl" : "ltr"}
      sx={{
        position: "fixed",
        bottom: 24,
        [rtl ? "left" : "right"]: 24,
        width: { xs: "calc(100vw - 32px)", sm: 380 },
        height: 520,
        maxHeight: "calc(100vh - 48px)",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        overflow: "hidden",
        zIndex: 1300,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Avatar sx={{ bgcolor: "primary.dark", width: 32, height: 32 }}>
          <SmartToyIcon fontSize="small" />
        </Avatar>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {t("aiAssistant.title")}
        </Typography>
        {ttsSupported && (
          <Tooltip title={speakEnabled ? t("aiAssistant.muteReplies") : t("aiAssistant.speakReplies")}>
            <IconButton
              size="small"
              onClick={() => {
                if (speakEnabled) window.speechSynthesis.cancel();
                setSpeakEnabled((v) => !v);
              }}
              sx={{ color: "inherit" }}
            >
              {speakEnabled ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: "inherit" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box ref={scrollRef} sx={{ flexGrow: 1, overflowY: "auto", p: 2, bgcolor: "grey.50" }}>
        {messages.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
            {t("aiAssistant.greeting")}
          </Typography>
        )}
        <Stack spacing={1.5}>
          {messages.map((m, i) => (
            <Box
              key={i}
              sx={{
                alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                bgcolor: m.role === "user" ? "primary.main" : "background.paper",
                color: m.role === "user" ? "primary.contrastText" : "text.primary",
                px: 1.5,
                py: 1,
                borderRadius: 2,
                maxWidth: "85%",
                boxShadow: 1,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              <Typography variant="body2">{m.content}</Typography>
            </Box>
          ))}
          {loading && (
            <Box sx={{ alignSelf: "flex-start", px: 1.5, py: 1 }}>
              <CircularProgress size={18} />
            </Box>
          )}
        </Stack>
      </Box>

      {error && (
        <Typography variant="caption" color="error" sx={{ px: 2, py: 0.5 }}>
          {error}
        </Typography>
      )}

      {/* Composer */}
      <Box sx={{ p: 1, display: "flex", alignItems: "center", gap: 0.5, borderTop: 1, borderColor: "divider" }}>
        {speechSupported && (
          <Tooltip title={listening ? t("aiAssistant.stopListening") : t("aiAssistant.speak")}>
            <IconButton color={listening ? "error" : "default"} onClick={toggleListening}>
              {listening ? <MicOffIcon /> : <MicIcon />}
            </IconButton>
          </Tooltip>
        )}
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={3}
          placeholder={t("aiAssistant.placeholder")}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
        />
        <IconButton color="primary" onClick={() => send(input)} disabled={loading || !input.trim()}>
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default AiAssistantWidget;
